import { Injectable, ComponentRef, ApplicationRef, EnvironmentInjector, EmbeddedViewRef, createComponent, Type } from '@angular/core';
import { Notification } from '@sukna/shared';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private activeNotifications = new Set<ComponentRef<Notification>>();

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) { }

  show(options: {
    color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
    title?: string;
    message?: string;
    icon?: 'question' | 'success' | 'error' | 'warning' | 'info';
    iconComponent?: Type<any>; //pass icon component that are placed in shared > icon
    iconColor?: string;
    position?: 'top' | 'top-start' | 'top-end' | 'top-left' | 'top-right' | 'center' | 'center-start' | 'center-end' | 'center-left' | 'center-right' | 'bottom' | 'bottom-start' | 'bottom-end' | 'bottom-left' | 'bottom-right';
    showCloseButton?: boolean;
    showConfirmButton?: boolean;
    duration?: number;
  } = {}) {
    options.showCloseButton = true;
    const componentRef = createComponent(Notification, {
      environmentInjector: this.injector
    });

    const instance: Notification = componentRef.instance;
    if (options.color !== undefined) instance.color = options.color;
    if (options.title !== undefined) instance.title = options.title;
    if (options.message !== undefined) instance.message = options.message;
    if (options.icon !== undefined) instance.icon = options.icon;
    if (options.iconComponent !== undefined) instance.iconComponent = options.iconComponent;
    if (options.iconColor !== undefined) instance.iconColor = options.iconColor;
    if (options.position !== undefined) instance.position = options.position;
    if (options.showCloseButton !== undefined) instance.showCloseButton = options.showCloseButton;
    if (options.showConfirmButton !== undefined) instance.showConfirmButton = options.showConfirmButton;
    if (options.duration !== undefined) instance.duration = options.duration;

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    componentRef.changeDetectorRef.detectChanges();

    instance.showToast();

    // Track the active notification
    this.activeNotifications.add(componentRef);

    if (options.duration && options.duration > 0) {
      setTimeout(() => {
        this.remove(componentRef, domElem);
      }, options.duration);
    }

    return componentRef;
  }

  private remove(componentRef: ComponentRef<Notification>, domElem: HTMLElement) {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
    domElem.remove();
    this.activeNotifications.delete(componentRef);
  }

  close(componentRef: ComponentRef<Notification>) {
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    this.remove(componentRef, domElem);
  }

  closeAll() {
    Swal.close()
    const notifications = Array.from(this.activeNotifications);
    notifications.forEach(componentRef => {
      const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      this.remove(componentRef, domElem);
    });
  }

  /** Ensures toast body is never coerced to "[object Object]" from a stray object. */
  private static toMessageString(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  success(title: string, message?: string, options: Partial<Parameters<typeof this.show>[0]> = {}) {
    setTimeout(() => {
      this.show({
        ...options,
        color: 'success',
        title:'',
        message: NotificationService.toMessageString(message)
      });
    }, 0);
  }

  error(title: string, message?: string, options: Partial<Parameters<typeof this.show>[0]> = {}) {
    setTimeout(() => {
      this.show({
        ...options,
        color: 'danger',
        title:'',
        message: NotificationService.toMessageString(message)
      });
    }, 0);
  }

  warning(title: string, message?: string, options: Partial<Parameters<typeof this.show>[0]> = {}) {
    setTimeout(() => {
      this.show({
        ...options,
        color: 'warning',
        title:'',
        message: NotificationService.toMessageString(message)
      });
    }, 0);
  }

  info(title: string, message?: string, options: Partial<Parameters<typeof this.show>[0]> = {}) {
    setTimeout(() => {
      this.show({
        ...options,
        color: 'info',
        title:'',
        message: NotificationService.toMessageString(message)
      });
    }, 0);
  }
}
